import React, {useState, Fragment} from 'react';

import {includes} from 'lodash-es';
import PropTypes from 'prop-types';

import {
  AGATHA_PAGE_SEARCH_FORM_ID,
  GLOBAL_NAV_SEARCH_FORM_ID,
  CLASSNAME_FOR_AGATHA_GENERATED_SUGGESTION,
} from 'static/three-oh/src/routes/Search/constants';
import {eventStream} from 'static/three-oh/utils/sessionActivity';

import styles from './styles';

const componentDisplayName = 'AgathaSuggestion';
const propTypes = {
  applyFocusToNextSuggestion: PropTypes.func.isRequired,
  applyFocusToPreviousSuggestion: PropTypes.func.isRequired,
  suggestion: PropTypes.object.isRequired,
  dataIndexNumber: PropTypes.number.isRequired,
  isGlobalNavSearch: PropTypes.bool.isRequired,
  updateSuggestionsAreVisible: PropTypes.func,
  onSuggestionSelection: PropTypes.func.isRequired,
  fragmentForSuggestionsFetch: PropTypes.string.isRequired,
};

const defaultProps = {
  isGlobalNavSearch: false,
};

const AgathaSuggestion = (props) => {
  const {
    applyFocusToNextSuggestion,
    applyFocusToPreviousSuggestion,
    suggestion,
    dataIndexNumber,
    isGlobalNavSearch,
    updateSuggestionsAreVisible,
    onSuggestionSelection,
    fragmentForSuggestionsFetch,
  } = props;
  const [suggestionIsSelected, updateSuggestionIsSelected] = useState(false);
  const searchPageInputField = document.getElementById(AGATHA_PAGE_SEARCH_FORM_ID);
  const globalNavInputField = document.getElementById(GLOBAL_NAV_SEARCH_FORM_ID);
  const inputFieldElement = isGlobalNavSearch ? globalNavInputField : searchPageInputField;

  const handleSuggestionSelection = (event) => {
    // Send data about this selection to Kinesis.
    const legacyEventProperties = {
      objectType: 'Search Suggestion',
      actionPrefix: 'Initiate',
      actionName: 'Search',
      captured_url: window.location.pathname,
      trackImpression: true,
      eventProperties: {
        suggestions_id: suggestion.suggestionId,
        search_term: suggestion.text,
      },
    };
    const eventStreamProperties = {
      description: `The ${suggestion.text} suggestion was selected when this fragment appeared in the search form: ${fragmentForSuggestionsFetch}`,
      input_needle: fragmentForSuggestionsFetch,
      suggestion_text: suggestion.text,
      current_url: window.location.pathname,
      application: 'agatha',
      search_id: suggestion.suggestionId,
      schema_version: '1.0',
      component_type: componentDisplayName,
    };
    eventStream(eventStreamProperties, legacyEventProperties);

    onSuggestionSelection(suggestion);
    updateSuggestionsAreVisible(false);
  };

  const onSuggestionKeyDown = (event) => {
    if (includes(['Enter', ' '], event.key)) {
      handleSuggestionSelection(event);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      applyFocusToNextSuggestion(dataIndexNumber);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      applyFocusToPreviousSuggestion(dataIndexNumber);
    } else if (event.key === 'Tab' && inputFieldElement) {
      // Per a11y guidelines, if the tab key is selected on a suggestion, apply
      // focus to the search form's input field.
      inputFieldElement.focus();
    }
  };

  // Embolden fragments of the suggestion that don't match with the input needle.
  const formatSuggestionText = () => {
    const subString = fragmentForSuggestionsFetch.toLowerCase();
    const suggestionArray = suggestion.text.split(subString);

    return (
      <span>
        {suggestionArray.map((item, index) => (
          <Fragment key={index}>
            <b>{item}</b>
            {
              index !== suggestionArray.length - 1 &&
              subString
            }
          </Fragment>
        ))}
      </span>
    );
  };

  const onSuggestionMouseEnter = (event) => {
    event.currentTarget.focus();
  };

  return (
    <li
      className={`${CLASSNAME_FOR_AGATHA_GENERATED_SUGGESTION} ${styles.suggestion}`}
      onClick={handleSuggestionSelection}
      onMouseEnter={onSuggestionMouseEnter}
      onKeyDown={onSuggestionKeyDown}
      data-index-number={dataIndexNumber}
      onFocus={() => updateSuggestionIsSelected(true)}
      onBlur={() => updateSuggestionIsSelected(false)}
      tabIndex={-1}
      aria-selected={suggestionIsSelected}
      role="option"
    >
      <div className={styles.textInOverlay}>
        {formatSuggestionText()}
      </div>
    </li>
  );
};

AgathaSuggestion.propTypes = propTypes;
AgathaSuggestion.componentDisplayName = componentDisplayName;
AgathaSuggestion.defaultProps = defaultProps;

export default AgathaSuggestion;
